/**
 * The lead funnel's SECOND layer — `pipeline_contact_status.lead_status_detail`.
 *
 * WI d77532fc collapsed the ladder from twelve rungs to seven and moved what the
 * retired ones carried down here: `connected` became the `replied` detail of
 * Engaged, and `unqualified` / `bad_timing` / `do_not_contact` became details of
 * Closed. Unlike the rung — which is tenant data, resolved against
 * `GET /lead-milestones` — a detail is a CLOSED vocabulary living in
 * `shared/config/lead_status_details.config.json`, so every fact about one
 * (its group, its tone, whether a rep may set it) comes from the generated
 * bindings and never from a literal here.
 *
 * This module is the client-side mirror of
 * `LeadMilestoneService.validate_detail_or_raise`: same five refusals, one layer
 * earlier, so a rep meets a disabled control rather than a 400.
 */
import {
  LEAD_STATUS_DETAIL_VALUES,
  leadStatusDetailGroup,
  leadStatusDetailManuallySettable,
  leadStatusDetailTone,
  type LeadStatusDetail,
} from '../../../generated/config-enums';
import type { StatusTone } from '../../../shared/components/common/StatusChip';

export type { LeadStatusDetail };

/**
 * Just enough of i18next's `t` for a label helper, spelled structurally rather
 * than imported — the same shape `shipments/utils/peopleRoles.ts` uses, so a
 * caller hands the hook's own `t` straight in with no cast and no dependency on
 * i18next's generic signature.
 */
type TFunction = (key: string, options?: Record<string, unknown>) => string;
/** The vocabulary itself, re-exported so a surface has ONE import for this layer. */
export { LEAD_STATUS_DETAIL_VALUES };

/**
 * The groups the vocabulary declares, in config order.
 *
 * Derived rather than listed: a rung's `detail_group` is free-form text on the
 * milestone row, and the only groups a picker can ever populate are the ones some
 * detail actually belongs to. A hand-written `['engaged', 'closed']` would be
 * right today and silently short the day a third group is seeded.
 */
export const LEAD_STATUS_DETAIL_GROUPS: readonly string[] = Array.from(
  new Set(LEAD_STATUS_DETAIL_VALUES.map((value) => leadStatusDetailGroup[value]))
);

/** Is this stored string a member of the vocabulary at all? */
export function isLeadStatusDetail(
  value: string | null | undefined
): value is LeadStatusDetail {
  return (
    !!value && (LEAD_STATUS_DETAIL_VALUES as readonly string[]).includes(value)
  );
}

/**
 * The details a rung declaring `group` offers, in config order.
 *
 * `manuallySettableOnly` is the default because the only caller that wants the
 * whole set is a RESOLVER (labelling a detail an automatic producer wrote), never
 * a picker: offering `do_not_contact` would let a rep record a suppression the
 * sequence engine never consults — it reads the tenant's shared email suppression
 * list, not this column.
 */
export function leadStatusDetailsForGroup(
  group: string | null | undefined,
  options?: { includeAutomaticOnly?: boolean }
): LeadStatusDetail[] {
  if (!group) return [];
  return LEAD_STATUS_DETAIL_VALUES.filter(
    (value) =>
      leadStatusDetailGroup[value] === group &&
      (options?.includeAutomaticOnly || leadStatusDetailManuallySettable[value])
  );
}

/**
 * Is the lead's CURRENT detail one only the suppression list can lift?
 *
 * The other half of the vocabulary's `manually_settable: false`, and the easier
 * half to miss: refusing the VALUE but allowing the move would let a rep quietly
 * re-open a suppressed lead by picking "bad timing" instead. The server refuses
 * any hand-edit of such a lead outright, so every status control mirrors it by
 * going read-only.
 */
export function isLeadStatusDetailLocked(detail: string | null | undefined): boolean {
  return isLeadStatusDetail(detail) && !leadStatusDetailManuallySettable[detail];
}

/** The chip hue for a detail, single-sourced from the config's `tone` metadata. */
export function leadStatusDetailToneOf(detail: LeadStatusDetail): StatusTone {
  return leadStatusDetailTone[detail] as StatusTone;
}

/**
 * A detail's display label.
 *
 * Takes the translator rather than returning a string, per the
 * `.claude/rules/rules-frontend.md` display-helper contract — a `.ts` module is
 * the one place `i18next/no-literal-string` cannot see an English literal. The
 * label map is `pipeline:leadStatusDetails`, pinned to the config's value set by
 * `tools/check_config_enum_i18n_label_map.py` so a seventh detail cannot render
 * its raw key.
 */
export function leadStatusDetailLabel(detail: string, t: TFunction): string {
  return t(`pipeline:leadStatusDetails.${detail}`, { defaultValue: detail });
}

/**
 * The two layers as ONE chip label — "Closed · Bad Timing".
 *
 * Composed into the single `label` `StatusChipSelect` takes, rather than drawn as
 * a second chip beside it: the chip IS the control (STYLE_GUIDE § Changeable
 * Status Chips), so a sibling chip would either be dead decoration outside the
 * trigger or a second control for one field. The separator lives in the locale
 * string so a translator owns it and RTL lays it out with the rest of the
 * sentence.
 */
export function formatLeadStatusWithDetail(
  statusLabel: string,
  detail: string | null | undefined,
  t: TFunction
): string {
  if (!detail) return statusLabel;
  return t('pipeline:leads.statusWithDetail', {
    status: statusLabel,
    detail: leadStatusDetailLabel(detail, t),
  });
}
